import { createSelector, createSlice, type PayloadAction } from '@reduxjs/toolkit'

import { resourceScopeCleared, userScopeCleared } from '@krp/client-runtime'

import { dropResource, ridKey } from '../../../shared/model/keys'

/**
 * Подтверждение кода кассира (RP-FB22 4.2) и блокировка после неверных кодов. Сам код сюда не попадает:
 * он живёт в поле ввода до отправки и отдаётся службе одним запросом. Здесь — только подпись службы со сроком
 * (`proof`, ≤ 10 минут) и время, до которого служба держит кассира заблокированным.
 *
 * Срок — `session`: F5 возвращает подтверждение без нового ввода, закрытие вкладки — стирает. Сообщение о последней
 * проверке (`checks`) — в памяти вкладки и в хранилище не пишется.
 */
export interface CashierProofRecord {
  proof: string
  /** ISO. */
  expiresAt: string
}

export type CashierCheckPhase = 'checking' | 'wrong' | 'expired' | 'error'

export interface CashierCheck {
  phase: CashierCheckPhase
  attemptsLeft: number | null
  message: string | null
}

export interface CashierAccessState {
  /** Ключ — `<rid>|<кассир>`. */
  proofs: Record<string, CashierProofRecord>
  /** До какого времени (ISO) служба держит кассира заблокированным. */
  locks: Record<string, string>
  /** Итог последней проверки — только на экране. */
  checks: Record<string, CashierCheck>
}

const initialState: CashierAccessState = { proofs: {}, locks: {}, checks: {} }

export const accessKeyOf = (resourceId: string, cashierId: string) => ridKey(resourceId, cashierId.toLowerCase())

interface Ref {
  resourceId: string
  cashierId: string
}

const cashierAccessSlice = createSlice({
  name: 'aqsi/cashierAccess',
  initialState,
  reducers: {
    checkStarted(state, action: PayloadAction<Ref>) {
      state.checks[accessKeyOf(action.payload.resourceId, action.payload.cashierId)] = {
        phase: 'checking',
        attemptsLeft: null,
        message: null,
      }
    },
    proofGranted(state, action: PayloadAction<Ref & CashierProofRecord>) {
      const key = accessKeyOf(action.payload.resourceId, action.payload.cashierId)
      state.proofs[key] = { proof: action.payload.proof, expiresAt: action.payload.expiresAt }
      delete state.checks[key]
      delete state.locks[key]
    },
    codeWrong(state, action: PayloadAction<Ref & { attemptsLeft: number | null }>) {
      state.checks[accessKeyOf(action.payload.resourceId, action.payload.cashierId)] = {
        phase: 'wrong',
        attemptsLeft: action.payload.attemptsLeft,
        message: null,
      }
    },
    cashierLocked(state, action: PayloadAction<Ref & { lockedUntil: string }>) {
      const key = accessKeyOf(action.payload.resourceId, action.payload.cashierId)
      state.locks[key] = action.payload.lockedUntil
      delete state.checks[key]
    },
    checkFailed(state, action: PayloadAction<Ref & { message: string }>) {
      state.checks[accessKeyOf(action.payload.resourceId, action.payload.cashierId)] = {
        phase: 'error',
        attemptsLeft: null,
        message: action.payload.message,
      }
    },
    /** Подтверждение больше не годится (истекло или служба его не приняла): код надо ввести ещё раз. */
    proofDropped(state, action: PayloadAction<Ref & { message: string }>) {
      const key = accessKeyOf(action.payload.resourceId, action.payload.cashierId)
      delete state.proofs[key]
      state.checks[key] = { phase: 'expired', attemptsLeft: null, message: action.payload.message }
    },
    /** Срок вышел: убрать просроченные подтверждения и блокировки. `now` — миллисекунды. */
    accessPruned(state, action: PayloadAction<{ now: number }>) {
      for (const [key, record] of Object.entries(state.proofs)) {
        if (Date.parse(record.expiresAt) <= action.payload.now) {
          delete state.proofs[key]
          state.checks[key] = {
            phase: 'expired',
            attemptsLeft: null,
            message: 'Подтверждение кода истекло — введите код ещё раз.',
          }
        }
      }
      for (const [key, until] of Object.entries(state.locks)) {
        if (Date.parse(until) <= action.payload.now) delete state.locks[key]
      }
    },
    accessRestored(state, action: PayloadAction<{ proofs: Record<string, CashierProofRecord>; locks: Record<string, string> }>) {
      state.proofs = action.payload.proofs
      state.locks = action.payload.locks
    },
  },
  extraReducers: (builder) => {
    builder.addCase(userScopeCleared, () => initialState)
    builder.addCase(resourceScopeCleared, (state, action) => {
      dropResource(state.proofs, action.payload.resourceId)
      dropResource(state.locks, action.payload.resourceId)
      dropResource(state.checks, action.payload.resourceId)
    })
  },
})

export const { checkStarted, proofGranted, codeWrong, cashierLocked, checkFailed, proofDropped, accessPruned, accessRestored } =
  cashierAccessSlice.actions
export const cashierAccessReducer = cashierAccessSlice.reducer

/** Подтверждение, которое ещё в силе; `undefined` — нет или истекло. */
export function liveProofOf(
  state: CashierAccessState,
  resourceId: string,
  cashierId: string,
  now: number,
): CashierProofRecord | undefined {
  const record = state.proofs[accessKeyOf(resourceId, cashierId)]
  return record !== undefined && Date.parse(record.expiresAt) > now ? record : undefined
}

/** Что сохранять в хранилище вкладки: подтверждения и блокировки, без сообщений проверок. Ссылочно стабильно: запись идёт только при изменении. */
export const selectAccessToPersist = createSelector(
  [(state: CashierAccessState) => state.proofs, (state: CashierAccessState) => state.locks],
  (proofs, locks) => ({ proofs, locks }),
)

const isObject = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null && !Array.isArray(value)

/** Проверка прочитанного (D-072). Просроченное отбрасывается сразу: F5 возвращает только живое подтверждение. */
export function decodeAccess(raw: unknown): { proofs: Record<string, CashierProofRecord>; locks: Record<string, string> } | undefined {
  if (!isObject(raw) || !isObject(raw.proofs) || !isObject(raw.locks)) return undefined
  const now = Date.now()
  const proofs: Record<string, CashierProofRecord> = {}
  for (const [key, value] of Object.entries(raw.proofs)) {
    if (!isObject(value) || typeof value.proof !== 'string' || typeof value.expiresAt !== 'string') return undefined
    if (Date.parse(value.expiresAt) > now) proofs[key] = { proof: value.proof, expiresAt: value.expiresAt }
  }
  const locks: Record<string, string> = {}
  for (const [key, value] of Object.entries(raw.locks)) {
    if (typeof value !== 'string') return undefined
    if (Date.parse(value) > now) locks[key] = value
  }
  return { proofs, locks }
}
